import { AggregationFn } from "../../../components/TrendOverTimeChart/TrendsOverTimeChart.types";
import { Match } from "../../../directus/collections";
import {
  average,
  formatSecondsToDDHHMMSS,
  percentage,
} from "../../../libs/utils";
import { CalculatedStat } from "../../../types/match";
import { formatPercentage } from "../../../utils/cityEscapeSummary";
import { getOverallExperienceRating } from "../../../utils/matches";
import { CityEscapeOverTimeChartAggregationKey } from "./CityEscapeTrendsOverTimeChart.types";

export type ValueLog = {
  id?: string;
  timestamp: number;
  value: number | null;
};

export const AGGREGATION_KEYS: Record<
  CityEscapeOverTimeChartAggregationKey,
  {
    label: string;
    key: (match: Match) => number | number[] | ValueLog | ValueLog[] | null;
    defaultFn: AggregationFn;
    valueFormatter?: (value: number | null) => string | number;
    minValue?: number;
    maxValue?: number;
    /** Direction where increase is positive: 'up' = green on increase, 'down' = green on decrease */
    positiveDirection?: "up" | "down";
  }
> = {
  start: {
    label: "Avvii",
    key: (match) => new Date(match.date_created).getTime(),
    defaultFn: "count",
  },
  end: {
    label: "Conclusioni",
    key: (match) =>
      match.completed_at ? new Date(match.completed_at).getTime() : null,
    defaultFn: "count",
  },
  rating: {
    label: "Valutazione",
    key: getOverallExperienceRating,
    defaultFn: "average",
    minValue: 1,
    maxValue: 5,
  },
  duration: {
    label: "Durata Partita",
    key: (match) =>
      match.completed_at
        ? (new Date(match.completed_at).getTime() -
            new Date(match.date_created).getTime()) /
          1000
        : null,
    defaultFn: "average",
    valueFormatter: (value: number | null) =>
      formatSecondsToDDHHMMSS(value, { first: 2 }),
    positiveDirection: "down",
    minValue: 0,
  },
  enigmas: {
    label: "Numero Enigmi",
    key: (match) =>
      Object.values(match.calculated_challenge_stats || {}).length,
    defaultFn: "average",
  },
  hints: {
    label: "Indizi Usati",
    key: (match) =>
      average(
        Object.values(match.calculated_challenge_stats || {}).map((challenge) =>
          challenge.end ? challenge.hints.used : null
        )
      ),
    defaultFn: "average",
    positiveDirection: "down",
  },
  successes: {
    label: "Enigmi Risolti",
    key: (match) => {
      const statsList = Object.values(match.calculated_challenge_stats || {});
      const values = statsList.filter(
        (challenge) => !challenge.solution_asked
      ).length;
      return percentage(values, statsList.length);
    },
    defaultFn: "average",
    valueFormatter: (value: number | null) => formatPercentage(value),
    minValue: 0,
    maxValue: 100,
  },
  failures: {
    label: "Enigmi Non Risolti",
    key: (match) => {
      const statsList = Object.values(match.calculated_challenge_stats || {});
      const values = statsList.filter(
        (challenge) => challenge.solution_asked
      ).length;
      return percentage(values, statsList.length);
    },
    defaultFn: "average",
    valueFormatter: (value: number | null) => formatPercentage(value),
    minValue: 0,
    maxValue: 100,
    positiveDirection: "down",
  },
  enigma_duration: {
    label: "Durata Enigma",
    key: (match) => {
      const durations = Object.values(match.calculated_challenge_stats || {})
        .filter(
          (challenge): challenge is CalculatedStat & { end: string } =>
            challenge.end != null
        )
        .map((challenge) => ({
          id: `${match.id}@${challenge.id}`,
          timestamp: challenge.end,
          value: challenge.resolution_time,
        }));
      return durations;
    },
    defaultFn: "average",
    valueFormatter: (value: number | null) =>
      formatSecondsToDDHHMMSS(value, { first: 2 }),
    positiveDirection: "down",
    minValue: 0,
  },
};
